import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Container, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ArrowForward } from "@mui/icons-material";
import { useFetch } from "@/hooks";
import { companiesApi } from "@/api";
import CompanyCard from "@/components/CompanyCard";
import { HOME_HOW_IT_WORKS_STEPS, HOME_RATING_CATEGORY_ITEMS } from "@/constants/home";

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: companiesData } = useFetch(() => companiesApi.getAll(0, 4), []);

  return (
    <Box>
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: 10,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
            }}
          >
            {t("home.heroTitle")}
            <br />
            <Box component="span" sx={{ color: "primary.main" }}>
              {t("home.heroHighlight")}
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              mb: 4,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            {t("home.heroDescription")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate("/companies")}
            >
              {t("home.exploreCompanies")}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/companies")}
            >
              {t("home.viewTopRated")}
            </Button>
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: 8, borderTop: 1, borderColor: "divider" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            {t("home.ratingSystemTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            {t("home.ratingSystemDescription")}
          </Typography>
          <Grid container spacing={3}>
            {HOME_RATING_CATEGORY_ITEMS.map((cat, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={i}>
                <Card sx={{ textAlign: "center", height: "100%" }}>
                  <CardContent>
                    <Box sx={{ color: "primary.main", mb: 2 }}>{cat.icon}</Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {t(cat.labelKey)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      {companiesData && companiesData.items.length > 0 && (
        <Box sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
              }}
            >
              {t("home.topRatedTitle")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 4,
              }}
            >
              {t("home.topRatedDescription")}
            </Typography>
            <Grid container spacing={3}>
              {companiesData.items.map((company) => (
                <Grid size={{ xs: 12, md: 6 }} key={company.id}>
                  <CompanyCard company={company} />
                </Grid>
              ))}
            </Grid>
            <Box
              sx={{
                textAlign: "center",
                mt: 4,
              }}
            >
              <Button
                onClick={() => navigate("/companies")}
                endIcon={<ArrowForward />}
                sx={{ color: "primary.main" }}
              >
                {t("company.viewAll")}
              </Button>
            </Box>
          </Container>
        </Box>
      )}
      <Box sx={{ py: 8, borderTop: 1, borderBottom: 1, borderColor: "divider" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            {t("home.howItWorksTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            {t("home.howItWorksDescription")}
          </Typography>
          <Grid container spacing={4}>
            {HOME_HOW_IT_WORKS_STEPS.map((step, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={i}>
                <Card sx={{ textAlign: "center", height: "100%" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ color: "primary.main", mb: 2 }}>{step.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      {t(step.titleKey)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {t(step.descKey)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <Box sx={{ py: 10 }}>
        <Container maxWidth="sm" sx={{ textAlign: "center" }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            {t("home.ctaTitle")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
            }}
          >
            {t("home.ctaDescription")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button variant="contained" size="large" onClick={() => navigate("/login")}>
              {t("home.createAccount")}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/companies")}
            >
              {t("home.browseFirst")}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
